/**
 * OPEN-217 (DEC-382) — driver entry rules in review: the VALIDATION_RULE card's findings and the
 * "Driver confirmed warnings" list. Kept out of InReviewDetailPanel so both render and test on
 * their own.
 */

import * as React from 'react';
import {
  VALIDATION_RULE_FIELDS,
  type ValidationRuleField,
} from '@lodex-pro/shared/validation-rules';
import { formatCreated } from '../../lib/timestamp';
import type { DriverConfirmedWarning, ValidationRuleFinding } from './use-inreview';

/** The field's label, or the raw code for a field this build doesn't know. */
export function ruleFieldLabel(field: string): string {
  return field in VALIDATION_RULE_FIELDS
    ? VALIDATION_RULE_FIELDS[field as ValidationRuleField].label
    : field;
}

export const FINDING_REASON_TEXT: Record<ValidationRuleFinding['reason'], string> = {
  BLOCK_NOT_FIXED: 'Block not fixed on the phone',
  WARNING_NOT_CONFIRMED: 'Warning not confirmed',
  RULE_NOT_ON_PHONE: 'Rule wasn’t on the driver’s phone',
};

/** "Tank 2" for a per-tank finding; nothing for a ticket-level one. */
function where(position: number | null): string {
  return position === null ? '' : ` (tank ${String(position + 1)})`;
}

const item: React.CSSProperties = { fontSize: '12px', marginBottom: '2px' };

/** The VALIDATION_RULE card: each rule the phone didn't settle, and why. */
export function ValidationFindings({
  findings,
}: {
  findings: readonly ValidationRuleFinding[];
}): React.JSX.Element | null {
  if (findings.length === 0) return null;
  return (
    <ul style={{ margin: '4px 0 0', paddingLeft: '18px' }} data-testid="validation-findings">
      {findings.map((f, i) => (
        <li key={`${f.ruleId}-${String(i)}`} style={item}>
          <strong>
            {ruleFieldLabel(f.field)}
            {where(f.position)}
          </strong>
          : {f.message}{' '}
          <span style={{ color: 'var(--color-text-tertiary)' }}>
            — {FINDING_REASON_TEXT[f.reason]}
          </span>
        </li>
      ))}
    </ul>
  );
}

/** The Warnings the driver saw and confirmed, newest last, in the reviewer's time zone. */
export function DriverConfirmedWarnings({
  warnings,
  tz,
}: {
  warnings: readonly DriverConfirmedWarning[];
  tz: string;
}): React.JSX.Element {
  return (
    <ul style={{ margin: 0, paddingLeft: '18px' }} data-testid="driver-confirmed-warnings">
      {warnings.map((w, i) => (
        <li key={`${w.ruleId}-${String(i)}`} style={{ ...item, fontSize: '13px' }}>
          <strong>
            {ruleFieldLabel(w.field)}
            {where(w.position)}
          </strong>
          : “{w.message}”{' '}
          <span style={{ color: 'var(--color-text-tertiary)' }}>
            confirmed {formatCreated(w.confirmedAt, tz)}
          </span>
        </li>
      ))}
    </ul>
  );
}
